import {
  TBCTXKeysWithoutSize,
  TEPFCacheDTO,
  TInProgressDetails,
  TInProgressRoundDetails,
  TLineShiftInitialRound,
  TMasterdataItemInfoWithImagesTls,
  TRoamingQcDB,
  TRoamingQcEventWithoutStatus,
  TRoamingQcStore,
  TTransactionGroupedAudits,
} from '@monorepo/types';
import produce from 'immer';
import { BCTXComputationHelper } from './bctx-computation-helper';

export const RoamingQcUtil = {
  getEpfDetailsFromEvent(bulkEvent: TRoamingQcEventWithoutStatus): TEPFCacheDTO {
    return {
      epfData: {
        name: bulkEvent.events[0].eventInfo.auditDetails.operator,
      },
      lastUpdatedTime: bulkEvent.events[0].eventInfo.eventTime,
    };
  },

  getIdFromDefect(defect: TMasterdataItemInfoWithImagesTls) {
    return defect.category + '_' + defect.sub_category + '_' + defect.name;
  },

  getDefectsAddedFromEvent(bulkEvent: TRoamingQcEventWithoutStatus): TMasterdataItemInfoWithImagesTls[] {
    const set = new Set<string>();
    const filteredSamples: TMasterdataItemInfoWithImagesTls[] = [];
    bulkEvent.events[0].eventInfo.auditDetails.samples
      .filter((sample) => sample.defects.length > 0)
      .forEach((sample) => {
        sample.defects.forEach((defect) => {
          const id = RoamingQcUtil.getIdFromDefect(defect);
          if (!set.has(id)) {
            set.add(id);
            filteredSamples.push(defect);
          }
        });
      });
    return filteredSamples;
  },

  getMergedDefectsAndClearImagesFromCurrentList(currentList: TMasterdataItemInfoWithImagesTls[], newList: TMasterdataItemInfoWithImagesTls[]) {
    const set = new Set<string>();
    currentList.forEach((defect) => {
      const id = RoamingQcUtil.getIdFromDefect(defect);
      set.add(id);
    });
    return produce(currentList, (draft) => {
      draft.forEach(defect => {
        defect.capturedImages = null;
        defect.correctiveActions = null;
        defect.preventiveActions = null;
        defect.rootCause = null;
      });
      newList.forEach((defect) => {
        const id = RoamingQcUtil.getIdFromDefect(defect);
        if (!set.has(id)) {
          set.add(id);
          draft.push({ ...defect, capturedImages: null, correctiveActions: null, preventiveActions: null, rootCause: null });
        }
      });
    });
  },

  updateInProgressDetails(state: TRoamingQcDB, line: string, shift: string, inProgressDetails: TInProgressDetails) {
    return produce(state, (draft) => {
      if (!draft.dailyCache.inProgressDetails.lineMap[line]) {
        draft.dailyCache.inProgressDetails.lineMap[line] = {
          shiftMap: {
            [shift]: inProgressDetails,
          },
        };
      } else draft.dailyCache.inProgressDetails.lineMap[line].shiftMap[shift] = inProgressDetails;
    });
  },

  updateEpfDetailsAndRecentCategoryList(
    state: TRoamingQcDB,
    orderKeys: TBCTXKeysWithoutSize[],
    bulkEvent: TRoamingQcEventWithoutStatus,
  ) {
    const line = bulkEvent.metadata.stationDetails.subject.subject;
    const shift = bulkEvent.metadata.stationDetails.shift.id;
    const bctxKey = BCTXComputationHelper.getUniqueBctxKeyWithOrder(
      orderKeys,
      bulkEvent.events[0].eventInfo.auditDetails.bctxItemListWithoutSize,
    );
    state = produce(state, (draft) => {
      if (!draft.dailyCache.recentCategoryList[bctxKey]) draft.dailyCache.recentCategoryList[bctxKey] = [];
    });
    const recentDefectsWithoutImages = RoamingQcUtil.getMergedDefectsAndClearImagesFromCurrentList(
      state.dailyCache.recentCategoryList[bctxKey],
      RoamingQcUtil.getDefectsAddedFromEvent(bulkEvent),
    );
    state = produce(state, (draft) => {
      draft.dailyCache.recentCategoryList[bctxKey] = recentDefectsWithoutImages;
    });
    state = produce(state, (draft) => {
      if (!draft.persistanceCache.epfDetails.lineMap[line]) {
        draft.persistanceCache.epfDetails.lineMap[line] = {
          shiftMap: {
            [shift]: [],
          },
        };
      } else if (!draft.persistanceCache.epfDetails.lineMap[line].shiftMap[shift]) {
        draft.persistanceCache.epfDetails.lineMap[line].shiftMap[shift] = [];
      }
    });
    return produce(state, (draft) => {
      const toBeUpdatedEpfDetails = RoamingQcUtil.getEpfDetailsFromEvent(bulkEvent);
      const hasAlreadyAdded = draft.persistanceCache.epfDetails.lineMap[line].shiftMap[shift].findIndex(
        (epfDetails) => {
          return epfDetails.epfData.name === toBeUpdatedEpfDetails.epfData.name;
        },
      );
      if (hasAlreadyAdded > -1) {
        draft.persistanceCache.epfDetails.lineMap[line].shiftMap[shift][hasAlreadyAdded] = toBeUpdatedEpfDetails;
      } else draft.persistanceCache.epfDetails.lineMap[line].shiftMap[shift].push(toBeUpdatedEpfDetails);
    });
  },

  clearInProgressDetails(state: TRoamingQcDB, line: string, shift: string) {
    return produce(state, (draft) => {
      if (
        draft.dailyCache.inProgressDetails.lineMap[line] &&
        draft.dailyCache.inProgressDetails.lineMap[line].shiftMap[shift]
      ) {
        if (Object.keys(draft.dailyCache.inProgressDetails.lineMap[line].shiftMap).length == 1) {
          delete draft.dailyCache.inProgressDetails.lineMap[line];
        } else delete draft.dailyCache.inProgressDetails.lineMap[line].shiftMap[shift];
      }
    });
  },

  getRoundInProgressDetails(bulkEvent: TRoamingQcEventWithoutStatus): TInProgressRoundDetails {
    return {
      type: 'IN_PROGRESS_ROUND',
      round: bulkEvent.events[0].eventInfo.auditDetails.round,
      bctx: bulkEvent.events[0].eventInfo.auditDetails.bctxItemListWithoutSize,
    };
  },

  getInitialRoundInfoFromTransactions(transactions: TTransactionGroupedAudits[]) {
    let lastRoundCompleted = 0;
    transactions.forEach((transaction) => {
      if (transaction.audits[0].round > lastRoundCompleted) lastRoundCompleted = transaction.audits[0].round;
    });
    return lastRoundCompleted;
  },

  updateInitialRoundDetails(round: number, state: TRoamingQcDB, stationId: string, shift: string) {
    return produce(state, (draft) => {
      if (!draft.dailyCache.lineShiftWiseRoundInfo.lineMap[stationId]) {
        draft.dailyCache.lineShiftWiseRoundInfo.lineMap[stationId] = {
          shiftMap: {
            [shift]: round,
          },
        };
      } else draft.dailyCache.lineShiftWiseRoundInfo.lineMap[stationId].shiftMap[shift] = round;
    });
  },

  getCategory(item: TMasterdataItemInfoWithImagesTls, areDefectsWithNoCategory: boolean) {
    // In case of defects with no category, we are showing all defects under "Minor" category.
    return areDefectsWithNoCategory ? 'Minor' : item.category;
  },

  clearImagesAndActionsFromDefects(defects: TMasterdataItemInfoWithImagesTls[]) {
    const clearedDefects = defects.map(defect => ({
      ...defect,
      capturedImages: null,
      correctiveActions: null,
      preventiveActions: null,
      rootCause: null,
    }));
    return clearedDefects;
  }
};
